/**
 * @file: ReservationItem.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * A reservation's booked service item — the order's `ServiceSubject` captured at
 * booking time, minus the assignee (which only exists once the order is made;
 * inspection items are assigned when the order is created). A ReservationItem
 * with `assignedTo` set is a valid `ServiceSubject`, so the array seeds the
 * order directly.
 */

import {
  ConstructionServiceSubject,
  FaultServiceSubject,
  InspectionServiceSubject,
  OtherServiceSubject,
  ServiceSubjectAssignee,
} from '@euler/model/OrderDetail';

export type ReservationInspectionItem = Omit<
  InspectionServiceSubject,
  'assignedTo'
> & { assignedTo?: ServiceSubjectAssignee };

export type ReservationItem =
  | ReservationInspectionItem
  | ConstructionServiceSubject
  | FaultServiceSubject
  | OtherServiceSubject;

/** Parse the reservation's `items` JSON column into the structured array. */
export const parseReservationItems = (
  raw: string | null | undefined,
): ReservationItem[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ReservationItem[]) : [];
  } catch {
    return [];
  }
};

/** Serialize items back to the JSON string the update endpoint persists. */
export const serializeReservationItems = (items: ReservationItem[]): string =>
  JSON.stringify(items);
